import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { componentScope } from '../../../../../../../constants/component-scope';
import { $comment, $manualAuditIssueComment, commentType } from '../../../../../../../../../shared/constants/comment';
import { UserAclService } from '../../../../../../../services/user-acl.service';
import { $user } from '../../../../../../../../../shared/constants/user';
import { IManualEvaluationAuditListItem } from '../../../../../../../../../shared/interfaces/manual-audit.interface';
import { ManualEvaluationAuditType } from '../../../../../../../../../shared/constants/manual-audit-issues';
import { AclSecurityAdapter } from '../../../../../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../../../../shared/constants/security';
import { IUserServerResponse } from '../../../../../../../../../shared/interfaces/user.interface';
import { UserService } from '../../../../../../../services/user.service';
import { Origin } from '../../../../../../../../../shared/interfaces/scan.interface';
import { TenantService } from '../../../../../../../services/tenant.service';
import { FeatureFlagCollection } from '../../../../../../../../../shared/interfaces/feature-flag.interface';

@Component({
  selector: 'app-view-comments',
  templateUrl: './view-comments.component.html',
  styleUrls: ['./view-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ViewCommentsComponent implements OnInit, OnDestroy {
  private hasDeleteOwnCommentPermissions$: Observable<boolean>;
  private hasDeleteOtherCommentPermissions$: Observable<boolean>;
  private hasDeleteInternalCommentPermission$: Observable<boolean>;

  @Input() public scope: componentScope;
  @Input() public comments: IManualEvaluationAuditListItem[];
  @Input() public userOrigin: Origin;
  @Input() public evaluationOrigin: Origin;
  @Output() public requestCommentDeletion: EventEmitter<string> = new EventEmitter<string>();
  @Output() public requestCommentEdit: EventEmitter<{ commentId: string; message: string }> = new EventEmitter<{
    commentId: string;
    message: string;
  }>();

  public $comment: typeof $comment = $comment;
  public commentType: typeof ManualEvaluationAuditType;
  public subscriptions: Subscription;
  public editEnabled: boolean = false;
  private currentUserId: string;

  private isReadOnly: boolean = false;

  @Input()
  public get readOnly(): boolean {
    return this.isReadOnly;
  }

  public set readOnly(value: boolean) {
    this.isReadOnly = value === true;
    this.changeDetectorRef.markForCheck();
  }

  constructor(
    private userService: UserService,
    private userAclService: UserAclService,
    private changeDetectorRef: ChangeDetectorRef,
    private tenantService: TenantService,
  ) {
    this.commentType = ManualEvaluationAuditType;
    this.subscriptions = new Subscription();
  }

  private buildSecurityCheckSubscriptions(): void {
    this.hasDeleteOwnCommentPermissions$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useFunctionalActions({ [FunctionalArea.mt_comments]: [SecureAction.delete] })
          .check(),
      ),
    );

    this.hasDeleteOtherCommentPermissions$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useForNewSecurityOnly()
          .useFunctionalActions({ [FunctionalArea.mt_comments_admin]: [SecureAction.delete] })
          .check(),
      ),
    );

    this.hasDeleteInternalCommentPermission$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useForNewSecurityOnly()
          .useFunctionalActions({ [FunctionalArea.mt_manual_finding_reviews]: [SecureAction.delete] })
          .check(),
      ),
    );
  }

  public canDelete$(comment: IManualEvaluationAuditListItem): Observable<boolean> {
    return combineLatest([
      this.userService.userDataChanged$,
      this.hasDeleteOtherCommentPermissions$,
      this.hasDeleteOwnCommentPermissions$,
      this.hasDeleteInternalCommentPermission$,
    ]).pipe(
      map(
        ([user, hasDeleteOtherCommentPermission, hasDeleteOwnCommentPermission, hasDeleteInternalCommentPermission]: [
          IUserServerResponse,
          boolean,
          boolean,
          boolean,
        ]): boolean => {
          const isCommentOwner: boolean = String(comment[$comment.user][$user._id]) === String(user[$user._id]);
          const cannotDelete: boolean =
            comment[$comment.type] === this.commentType.statusUpdate ||
            comment[$comment.type] === this.commentType.addFlag ||
            comment[$comment.type] === this.commentType.removeFlag;

          if (cannotDelete) {
            return false;
          }

          if (isCommentOwner) {
            const canDeleteInternal: boolean = comment[$manualAuditIssueComment.internal] && hasDeleteInternalCommentPermission;
            const canDeleteNormal: boolean = !comment[$manualAuditIssueComment.internal] && hasDeleteOwnCommentPermission;
            return canDeleteInternal || canDeleteNormal;
          }

          return (
            hasDeleteOtherCommentPermission &&
            !comment[$manualAuditIssueComment.internal] &&
            this.userOrigin === this.evaluationOrigin
          );
        },
      ),
    );
  }

  public onCommentDelete(commentId: string): void {
    this.requestCommentDeletion.emit(commentId);
  }

  public canEdit(comment: IManualEvaluationAuditListItem): boolean {
    if (this.editEnabled === false || this.readOnly || comment[$comment.type] !== commentType.manualAuditIssueComment) {
      return false;
    }

    return String(comment[$comment.user]?.[$user._id]) === String(this.currentUserId);
  }

  public onCommentEdited(comment: IManualEvaluationAuditListItem, message: string): void {
    comment[$manualAuditIssueComment.message] = message;
    comment[$comment.edited] = true;
    this.changeDetectorRef.markForCheck();
    this.requestCommentEdit.emit({
      commentId: String(comment[$comment._id]),
      message,
    });
  }

  public ngOnInit(): void {
    this.buildSecurityCheckSubscriptions();
    this.subscriptions.add(
      this.userService.userDataChanged$.subscribe((user: IUserServerResponse): void => {
        this.currentUserId = user?.[$user._id];
        this.changeDetectorRef.markForCheck();
      }),
    );
    this.subscriptions.add(
      this.tenantService
        .getTenantFeatureFlag(FeatureFlagCollection.allowEditTaskFindingsComments)
        .subscribe((enabled: boolean): void => {
          this.editEnabled = enabled;
          this.changeDetectorRef.markForCheck();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
